import { useEffect, useState, Fragment, useMemo } from 'react';
import { makeStyles, ThemeProvider, createTheme } from '@material-ui/core/styles';
import { Input, MenuItem, FormControl, ListItemText, Select, ListSubheader } from '@material-ui/core';

import { CheckBoxOutlined, CheckBoxOutlineBlankOutlined } from '@material-ui/icons';
import Chips from 'components/common/Chips';
import DelegationStatement from 'components/common/DelegationStatemet';
import ChevronIcon from 'components/common/ChevronFaIcon';
import { Conditional } from '../../../../../../../common/components/layout/index';
import { difference, isArray, isEmpty, uniqBy } from 'lodash';
import CustomizedMenus from 'components/common/CustomSelect/index';

const theme = createTheme({
    props: {
        MuiButtonBase: {
            disableRipple: true,
        },
    },
});

const ThemedMenuItem = (props) => (
    <ThemeProvider theme={theme}>
        <MenuItem {...props}>{props.children}</MenuItem>
    </ThemeProvider>
);


const useStyles = makeStyles((theme) => ({
    wrapper: {
        paddingTop: 10
    },
    formControl: {
        width: '100%',
    },
    Select: {
        '&:after': {
            borderBottom: 'none'
        }
    },
    header: {
        color: '#28282E',
        fontFamily: 'Hind',
        fontSize: '1rem',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '20px',
        paddingBottom: '5px',
    },
    required: {
        fontSize: '1rem',
        fontWeight: '500',
        color: '#ff923d',
        marginLeft: '0.57143rem'
    },
    inputBase: {
        width: '100%',
        height: '40px',
        borderRadius: '4px',
        border: '1px solid #CCC !important',
        padding: '10px',
        minWidth: 120,
        '&:active, &:focus, &:hover': {
            borderColor: '#2057AD !important'
        },
        '&:before': {
            display: 'none',
        },
        '&:after': {
            display: 'none',
        },
        '& > div': {
            padding: '0px',
            color: '#28282E',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontFamily: 'Hind',
            fontSize: '16px',
            // fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '28px',
        },
        '& > div:focus': {
            backgroundColor: 'initial',
        },
    },

    errorInput: {
        width: '100%',
        borderRadius: '3px',
        border: '1px solid var(--red-500, #C33) !important',
        padding: '6px',
        minWidth: 120,
        '&:before': {
            display: 'none',
        },
        '&:after': {
            display: 'none',
        },
        '& > div': {
            padding: '0px',
            color: '#28282E',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontFamily: 'Hind',
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: 'normal',
        },
        '& > div:focus': {
            backgroundColor: 'initial',
        },
    },
    subHeader: {
        backgroundColor: '#2057AD',
        color: '#fff',
        padding: '0rem 0.5rem',
        height: '40px',
        fontWeight: 600,
        lineHeight: '20px',
        display: 'flex',
        alignItems: 'center',
    },
    MenuItems: {
        height: '40px',
        padding: '0px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontFamily: 'Hind',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '20px',
        color: '#404040',
        backgroundColor: '#fff !important'
    },
    disabledItem: {
        display: 'none'
    },
    NotFocusable: {
        '&:after': {
            borderBottom: 'none'
        }
    },
    Required: {
        color: '#F1913B',
    },
    CheckBoxes: {
        width: '1rem',
        height: '1rem',
    },
    ListItemText: {
        '& > span': {
            color: '#404040',
            fontSize: 16,
            fontFamily: 'Hind',
            fontWeight: '400',
            lineHeight: 20,
            wordWrap: 'break-word'
        }
    },
    ListSubheader: {
        background: '#2057AD',
        color: '#FFF',
        fontFamily: 'Hind',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 600
    },
    Paper: {
        width: '600px',
        marginTop: '12px',
        marginLeft: '-10px',
        maxHeight: '400px',
    }
}));

const TOGGLE_ALL = 'handle_add_remove_all';

export default function MultipleSelect({ options = [], initialStamentCondition = false, initialStatement, laterStamentCondition = false, laterStatement, label, placeHolder, handleChange, selector, canSelectAll, selectedValues = [], groupOptions = false, errorField, required = false }) {
    const classes = useStyles();
    const [value, setValue] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [open, setOpen] = useState(false);
    const [showSelectAll, setShowSelectAll] = useState(true);

    useEffect(() => {
        if (options.length === value.length) {
            setShowSelectAll(false);
        } else {
            setShowSelectAll(true);
        }
    }, [options, value]);

    useEffect(() => {
        if (selectedValues) {
            setSelectedOptions(selectedValues);
            setValue(selectedValues.map(el => el.value || el.label));
        }
    }, [selectedValues]);


    const handleOnChange = ({ target }) => {
        const valueId = target.value;

        const updatedValues = options.filter(opts => {
            return valueId.some(id => id === opts.value);
        });
        let groupUserToAdd = [];
        if (groupOptions) {
            let updatedGroupValues = groupOptions && groupOptions.filter(opts => {
                return valueId.some(id => id === opts.value);
            });
            updatedGroupValues.forEach((g) => {
                groupUserToAdd = [...groupUserToAdd, ...g.users];
            });
        }

        if (toggleAll(valueId)) {
            if (showSelectAll) {
                setSelectedOptions(options);
                setValue(options.map(el => el.value));
            } else {
                setSelectedOptions([]);
                setValue([]);
            }
            return;
        }

        setSelectedOptions(uniqBy([...updatedValues, ...groupUserToAdd], 'value'));
        setValue(valueId);
    };
    const toggleAll = (values) => {
        return values.some(option => option === TOGGLE_ALL);
    };

    const optionsForGroupSelect = useMemo(() => {
        return [{
            id: 1,
            label: 'Groups',
            options: groupOptions
        },
        {
            id: 2,
            label: 'Users',
            options: options
        }];
    }, [groupOptions, options]);

    function handleMultiOptionSelected(selectedValue) {
        if (!value) return;
        let newValue = [selectedValue.value, ...value];
        let newSelectedOption = Object.prototype.hasOwnProperty.call(selectedValue, 'users') ? [...selectedValue.users, ...selectedOptions] : [selectedValue, ...selectedOptions];

        if (value.includes(selectedValue.value)) {
            newValue = value.filter(val => val !== selectedValue.value);
            newSelectedOption = Object.prototype.hasOwnProperty.call(selectedValue, 'users')
                ? difference(selectedOptions, selectedValue.users)
                : selectedOptions.filter(option => option.value !== selectedValue.value);
        }
        setValue(newValue);
        setSelectedOptions(newSelectedOption);
    }

    function customFilter(input) {
        if (!input) return optionsForGroupSelect;
        return [{
            id: 1,
            label: 'Groups',
            options: groupOptions.filter(option => option.label.toLowerCase().includes(input.toLowerCase()))
        },
        {
            id: 2,
            label: 'Users',
            options: options.filter(option => option.label.toLowerCase().includes(input.toLowerCase()))
        }];
    }

    function handleOpen() {
        setOpen(true);
    }
    function handleClose() {
        handleChange(selectedOptions);
        setOpen(false);
    }

    function useCustomSelector(option) {
        const labelSelector = selector || 'label';
        return option[labelSelector];
    }

    const menuProps = {
        classes: {
            paper: classes.Paper,
        },
        anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left'
        },
        transformOrigin: {
            vertical: 'top',
            horizontal: 'left'
        },
        transitionDuration: 0,
        getContentAnchorEl: null
    };

    function handleItemDeleted(listItem) {
        setSelectedOptions(listItem);
        setValue(listItem.map(el => el.value));
        handleChange(listItem);
    }

    function handleMultiSelectClosed() {
        if (isArray(selectedOptions) && !isEmpty(selectedOptions)) {
            handleChange(selectedOptions);
        }
    }

    const CheckboxIcon = ({ option, options }) => {
        const isChecked = options.some(val => val === option.value);

        return isChecked ? <CheckBoxOutlined className={classes.CheckBoxes} /> : <CheckBoxOutlineBlankOutlined className={classes.CheckBoxes} />;
    };
    return (
        <div className={!groupOptions ? classes.wrapper : ''}>
            <Conditional condition={!groupOptions}>
                <p className={classes.header}> {label}
                    <Conditional condition={required}>
                        <span className={classes.required}>*Required</span>
                    </Conditional>
                </p>
            </Conditional>
            <DelegationStatement type="warning" size="small" condition={initialStamentCondition}>
                {initialStatement}
            </DelegationStatement>
            <Chips data={selectedOptions} handleDelete={handleItemDeleted} />
            <FormControl className={classes.formControl}>
                <Conditional condition={!groupOptions}>
                    <Select
                        labelId="mutiple-checkbox-label"
                        id="mutiple-checkbox"
                        multiple
                        onOpen={handleOpen}
                        onClose={handleClose}
                        value={value}
                        open={open}
                        className={classes.inputBase}
                        onChange={handleOnChange}
                        input={<Input />}
                        renderValue={() => `${placeHolder}`}
                        IconComponent={() => <ChevronIcon isOpen={open} />}
                        displayEmpty={!groupOptions}
                        MenuProps={menuProps}
                        PaperProps={{ transitionDuration: 0 }}
                    >
                        <ThemedMenuItem style={{ display: `${canSelectAll ? 'block' : 'none'}`, fontFamily: 'Hind' }} value={TOGGLE_ALL}>{showSelectAll ? 'Select All' : 'Deselect All'}</ThemedMenuItem>
                        {options.map((option) => (
                            <ThemedMenuItem className={classes.MenuItems} key={option.value} value={option.value}>
                                <CheckboxIcon option={option} options={value} />
                                <span className={classes.ListItemText}>{useCustomSelector(option)}</span>
                            </ThemedMenuItem>
                        ))}
                    </Select>
                </Conditional>
                <Conditional condition={groupOptions}>
                    <CustomizedMenus
                        label={label}
                        placeholder={label}
                        options={optionsForGroupSelect}
                        isRequired={true}
                        onClose={handleMultiSelectClosed}
                        notValid={errorField}
                        customFilter={customFilter}
                        renderChild={({ filteredOptions }) => (
                            <section>
                                {
                                    Array.isArray(filteredOptions) && filteredOptions.map(({ id, label, options }) => (
                                        options.length > 0 && (
                                            <Fragment key={id}>
                                                <ListSubheader className={classes.ListSubheader}>{label}</ListSubheader>
                                                {
                                                    options.map((option) => (
                                                        <ThemedMenuItem onClick={() => handleMultiOptionSelected(option)} className={classes.MenuItems} key={option.id} value={option.value}>
                                                            <CheckboxIcon option={option} options={value} />
                                                            <ListItemText className={classes.ListItemText} primary={useCustomSelector(option)} />
                                                        </ThemedMenuItem>
                                                    ))
                                                }
                                            </Fragment>
                                        )
                                    ))
                                }
                            </section>
                        )}
                    />
                </Conditional>
            </FormControl>
            <DelegationStatement type="warning" size="small" condition={laterStamentCondition}>
                {laterStatement}
            </DelegationStatement>
        </div>
    );
}
